"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic,
  Code,
  Wand2,
  Layers,
  Sparkles,
  ChevronRight,
  Play,
  Pause,
} from "lucide-react";
import { Button } from "../ui/Button";
import GradientText from "../core/gradient-text/GradientText";
import { SectionHeader } from "../core/section/SectionHeader";
interface Props {
  isAuthed: boolean;
}
export function LiveEditingShowcase({ isAuthed }: Props) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [voiceText, setVoiceText] = useState("");

  const voiceCommands = [
    "Add a hero section with a gradient background",
    "Make the heading larger and change to purple",
    "Add a sign-up form below the text",
  ];

  const editorSteps = [
    {
      html: `<div class="container">
  <h1>Welcome to my website</h1>
  <p>This is a simple landing page.</p>
</div>`,
      result: (
        <div className="p-4 bg-white text-black">
          <h1 className="text-2xl font-bold mb-2">Welcome to my website</h1>
          <p>This is a simple landing page.</p>
        </div>
      ),
    },
    {
      html: `<div class="container">
  <div class="hero bg-gradient-to-r from-blue-500 to-purple-600 p-8 rounded-lg">
    <h1>Welcome to my website</h1>
    <p>This is a simple landing page.</p>
  </div>
</div>`,
      result: (
        <div className="p-4 bg-white text-black">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-8 rounded-lg text-white">
            <h1 className="text-2xl font-bold mb-2">Welcome to my website</h1>
            <p>This is a simple landing page.</p>
          </div>
        </div>
      ),
    },
    {
      html: `<div class="container">
  <div class="hero bg-gradient-to-r from-blue-500 to-purple-600 p-8 rounded-lg">
    <h1 class="text-4xl font-bold text-purple-200 mb-4">Welcome to my website</h1>
    <p>This is a simple landing page.</p>
  </div>
</div>`,
      result: (
        <div className="p-4 bg-white text-black">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-8 rounded-lg text-white">
            <h1 className="text-4xl font-bold text-purple-200 mb-4">
              Welcome to my website
            </h1>
            <p>This is a simple landing page.</p>
          </div>
        </div>
      ),
    },
    {
      html: `<div class="container">
  <div class="hero bg-gradient-to-r from-blue-500 to-purple-600 p-8 rounded-lg">
    <h1 class="text-4xl font-bold text-purple-200 mb-4">Welcome to my website</h1>
    <p>This is a simple landing page.</p>
    <form class="mt-6 bg-white/10 p-4 rounded-lg">
      <input type="email" placeholder="Enter your email" class="w-full p-2 rounded mb-2" />
      <button class="bg-purple-700 text-white px-4 py-2 rounded">Sign Up</button>
    </form>
  </div>
</div>`,
      result: (
        <div className="p-4 bg-white text-black">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-8 rounded-lg text-white">
            <h1 className="text-4xl font-bold text-purple-200 mb-4">
              Welcome to my website
            </h1>
            <p>This is a simple landing page.</p>
            <form className="mt-6 bg-white/10 p-4 rounded-lg">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full p-2 rounded mb-2 text-black"
              />
              <button className="bg-purple-700 text-white px-4 py-2 rounded">
                Sign Up
              </button>
            </form>
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      if (currentStep < editorSteps.length - 1) {
        setCurrentStep((prev) => prev + 1);
        simulateVoiceCommand(currentStep);
      } else {
        setCurrentStep(0);
        setIsVoiceActive(false);
        setVoiceText("");
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [isPlaying, currentStep]);

  const simulateVoiceCommand = (step: number) => {
    if (step >= voiceCommands.length) return;

    setIsVoiceActive(true);
    let text = "";
    const command = voiceCommands[step];

    const typeInterval = setInterval(() => {
      if (text.length < command.length) {
        text += command[text.length];
        setVoiceText(text);
      } else {
        clearInterval(typeInterval);
        setTimeout(() => {
          setIsVoiceActive(false);
          setVoiceText("");
        }, 2000);
      }
    }, 50);
  };

  const editorModes = [
    { name: "Code", icon: Code, color: "from-blue-500 to-cyan-400" },
    { name: "No-Code", icon: Wand2, color: "from-purple-500 to-pink-400" },
    { name: "Low-Code", icon: Layers, color: "from-amber-500 to-orange-400" },
  ];

  return (
    <section
      className="py-24 px-4 relative overflow-hidden backdrop-blur-sm shadow-card"
      id="live-editor"
    >
      <div className="absolute inset-0 z-0"></div>
      <div className="container mx-auto relative z-10 ">
        <div className="text-center mb-16 ">
          <SectionHeader
            title={
              <>
                {/* Who is raffaello For? <br /> */}
                <GradientText>Live Editor Demo</GradientText>
              </>
            }
            description={
              <p>
                Experience the power of Raffaello's Trinity Editor in action
              </p>
            }
          />
          {/* <h2 className="text-3xl md:text-5xl font-bold mb-10"> */}
          {/* <GradientText></GradientText> */}
          {/* </h2> */}
          {/* <p className="text-xl text-card-foreground/30 max-w-3xl mx-auto">
            Experience the power of Raffaello's Trinity Editor in action
          </p> */}
        </div>

        <div className="relative max-w-5xl mx-auto mb-20">
          <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 flex space-x-2 bg-gray-900/80 backdrop-blur-sm rounded-full p-1 border border-gray-700 z-20">
            {editorModes.map((mode, index) => (
              <div
                key={index}
                className={`px-4 py-2 rounded-full flex items-center space-x-2 ${index === 0 ? "bg-gray-800" : "hover:bg-gray-800/50"} cursor-pointer transition-colors`}
              >
                <mode.icon className="h-4 w-4" />
                <span className="text-sm font-medium">{mode.name}</span>
              </div>
            ))}
          </div>

          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 shadow-2xl"
          >
            <div className="bg-gray-800 px-4 py-3 flex items-center justify-between border-b border-gray-700">
              <div className="flex space-x-2">
                <div className="h-3 w-3 rounded-full bg-red-500"></div>
                <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                <div className="h-3 w-3 rounded-full bg-green-500"></div>
              </div>
              <div className="text-sm font-medium text-card-foreground/30">
                Raffaello Editor
              </div>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-700">
              <div className="p-4 font-mono text-sm overflow-auto h-[400px] relative">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <pre className="text-card-foreground/30">
                      <code>{editorSteps[currentStep].html}</code>
                    </pre>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="relative h-[400px] overflow-auto bg-gray-100">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="h-full"
                  >
                    {editorSteps[currentStep].result}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          <AnimatePresence>
            {isVoiceActive && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="absolute bottom-8 left-8 max-w-md bg-purple-900/90 backdrop-blur-sm rounded-lg p-4 border border-purple-500/50 shadow-lg shadow-purple-500/20 z-30"
              >
                <div className="flex items-start space-x-3">
                  <div className="relative">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                      <Mic className="h-5 w-5 text-white" />
                    </div>
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 0.8, 0.5],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                      }}
                      className="absolute -inset-1 rounded-full bg-purple-500/30 z-0"
                    ></motion.div>
                  </div>
                  <div>
                    <div className="text-sm text-purple-300 mb-1">
                      Voice Command
                    </div>
                    <div className="text-white font-medium">
                      {voiceText}
                      <span className="animate-pulse">|</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <Button
              href={isAuthed ? "" : "/signin"}
              className="absolute -bottom-6 right-8 px-5 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 font-medium shadow-lg shadow-purple-500/20 flex items-center space-x-2 cursor-pointer hover:opacity-90 transition-opacity"
            >
              <Sparkles className="h-4 w-4" />
              <span>Try the Editor</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
